import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/common/services/api/api.service';
import { ITravelAssistantMessage } from '../models/travel.assistant';

/**
 * Interface for a TravelBuddy chat message
 */
export interface TravelBuddyChatMessage {
  id?: string;
  type: 'USER' | 'AI' | 'AI_THINKING' | 'AI_ERROR' | 'AI_STREAM' | 'DATETIME';
  msg: string;
  name?: string;
  model?: string;
  suggestions?: string[];
  questions?: string[];
  errorMsg?: string;
  timeConsumed?: number;
  placeSearch?: any[];
  placesMentioned?: { name: string; city: string }[];
  eventsMentioned?: { name: string; city: string }[];
  askForEvents?: boolean;
  askForItinerary?: boolean;
  feedback?: 'good' | 'bad' | null;
  timestamp?: Date;
  isEditable?: boolean;
}

/**
 * Interface for a TravelBuddy chat session
 */
export interface TravelBuddyChatSession {
  id?: string;
  title: string;
  messages: TravelBuddyChatMessage[];
  createdAt?: Date;
  updatedAt?: Date;
  isSaved?: boolean;
  isRecent?: boolean;
  hubName?: string;
  userId?: string;
  ipAddress?: string;
  isAnonymous?: boolean;
  folderId?: string;
  metadata?: {
    assistantId?: string;
    threadId?: string;
    lastMessageAt?: Date;
  };
}

/**
 * Request payload for saving a chat
 */
export interface SaveChatRequest {
  title: string;
  messages: TravelBuddyChatMessage[];
  isSaved?: boolean;
  metadata?: {
    assistantId?: string;
    threadId?: string;
    lastMessageAt?: string;
  };
}

/**
 * Request payload for updating a chat
 */
export interface UpdateChatRequest {
  chatId?: string;
  title?: string;
  messages?: TravelBuddyChatMessage[];
  isSaved?: boolean;
  isRecent?: boolean;
  isInsert?: boolean;
  metadata?: {
    assistantId?: string;
    threadId?: string;
    lastMessageAt?: string;
  };
}

/**
 * Service for managing TravelBuddy chat sessions
 * Supports both logged-in users (user-based) and guests (IP-based)
 */
@Injectable({
  providedIn: 'root',
})
export class TravelBuddyChatService {
  constructor(private apiService: ApiService) {}

  // ==================== LOGGED-IN USER OPERATIONS ====================

  /**
   * Save a new chat session for a logged-in user
   */
  saveUserChat(
    hubName: string,
    userId: string,
    chatData: SaveChatRequest
  ): Observable<TravelBuddyChatSession> {
    return this.apiService.post(`visitors/${hubName}/users/${userId}/chats`, chatData).pipe(
      tap((chat) => console.log('User chat saved:', chat)),
      catchError((error) => {
        console.error('Error saving user chat:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all chat sessions for a logged-in user
   */
  getUserChats(
    hubName: string,
    userId: string,
    options?: { limit?: number; offset?: number; isSaved?: boolean }
  ): Observable<TravelBuddyChatSession[]> {
    const params: any = {};
    if (options?.limit) params.limit = options.limit;
    if (options?.offset) params.offset = options.offset;
    if (options?.isSaved !== undefined) params.isSaved = options.isSaved;

    return this.apiService.get(`visitors/${hubName}/users/${userId}/chats`, params).pipe(
      tap((chats) => console.log('User chats loaded:', chats.length)),
      catchError((error) => {
        console.error('Error loading user chats:', error);
        return of([]);
      })
    );
  }

  /**
   * Get a specific chat session by ID for a logged-in user
   */
  getUserChatById(
    hubName: string,
    userId: string,
    chatId: string
  ): Observable<TravelBuddyChatSession> {
    return this.apiService.get(`visitors/${hubName}/users/${userId}/chats/${chatId}`).pipe(
      tap((chat) => console.log('User chat loaded:', chat)),
      catchError((error) => {
        console.error('Error loading user chat:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing chat session for a logged-in user
   */
  updateUserChat(
    hubName: string,
    userId: string,
    updateData: UpdateChatRequest
  ): Observable<TravelBuddyChatSession> {
    return this.apiService.put(`visitors/${hubName}/users/${userId}/chats/`, updateData).pipe(
      tap((chat) => console.log('%c User chat updated:', 'color: yellow', chat)),
      catchError((error) => {
        console.error('Error updating user chat:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a chat session for a logged-in user
   */
  deleteUserChat(hubName: string, userId: string, chatId: string): Observable<void> {
    return this.apiService.delete(`visitors/${hubName}/users/${userId}/chats/${chatId}`).pipe(
      tap(() => console.log('User chat deleted:', chatId)),
      catchError((error) => {
        console.error('Error deleting user chat:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get recent chat sessions for a logged-in user (last N chats)
   */
  getUserRecentChats(
    hubName: string,
    userId: string,
    limit: number = 10
  ): Observable<TravelBuddyChatSession[]> {
    return this.apiService.get(`visitors/${hubName}/users/${userId}/chats/recent`, { limit }).pipe(
      tap((chats) => console.log('User recent chats loaded:', chats.length)),
      catchError((error) => {
        console.error('Error loading user recent chats:', error);
        return of([]);
      })
    );
  }

  /**
   * Delete all recent chat sessions for a logged-in user
   */
  deleteUserRecentChats(
    hubName: string,
    userId: string
  ): Observable<{ success: boolean; message: string; deletedCount: number }> {
    return this.apiService.delete(`visitors/${hubName}/users/${userId}/chats/recent`).pipe(
      tap((result) => console.log('User recent chats deleted:', result.deletedCount)),
      catchError((error) => {
        console.error('Error deleting user recent chats:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete all recent chat sessions for a guest user
   */
  deleteGuestRecentChats(
    hubName: string,
    ipAddress: string
  ): Observable<{ success: boolean; message: string; deletedCount: number }> {
    return this.apiService
      .delete(`visitors/${hubName}/guests/${encodeURIComponent(ipAddress)}/chats/recent`)
      .pipe(
        tap((result) => console.log('Guest recent chats deleted:', result.deletedCount)),
        catchError((error) => {
          console.error('Error deleting guest recent chats:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get saved/bookmarked chat sessions for a logged-in user
   */
  getUserSavedChats(hubName: string, userId: string): Observable<TravelBuddyChatSession[]> {
    return this.getUserChats(hubName, userId, { isSaved: true }).pipe(
      tap((chats) => console.log('User saved chats loaded:', chats.length))
    );
  }

  /**
   * Toggle the saved status of a chat for a logged-in user
   */
  toggleUserChatSaved(
    hubName: string,
    userId: string,
    chatId: string,
    isSaved: boolean
  ): Observable<TravelBuddyChatSession> {
    return this.updateUserChat(hubName, userId, { chatId, isSaved }).pipe(
      tap((chat) => console.log('User chat saved status toggled:', chat))
    );
  }

  // ==================== GUEST USER OPERATIONS (IP-based) ====================

  /**
   * Save a new chat session for a guest user (identified by IP address)
   */
  saveGuestChat(
    hubName: string,
    ipAddress: string,
    chatData: SaveChatRequest
  ): Observable<TravelBuddyChatSession> {
    return this.apiService
      .post(`visitors/${hubName}/guests/${encodeURIComponent(ipAddress)}/chats`, chatData)
      .pipe(
        tap((chat) => console.log('Guest chat saved:', chat)),
        catchError((error) => {
          console.error('Error saving guest chat:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get all chat sessions for a guest user
   */
  getGuestChats(
    hubName: string,
    ipAddress: string,
    options?: { limit?: number; offset?: number; isSaved?: boolean }
  ): Observable<TravelBuddyChatSession[]> {
    const params: any = {};
    if (options?.limit) params.limit = options.limit;
    if (options?.offset) params.offset = options.offset;
    if (options?.isSaved !== undefined) params.isSaved = options.isSaved;

    return this.apiService
      .get(`visitors/${hubName}/guests/${encodeURIComponent(ipAddress)}/chats`, params)
      .pipe(
        tap((chats) => console.log('Guest chats loaded:', chats.length)),
        catchError((error) => {
          console.error('Error loading guest chats:', error);
          return of([]);
        })
      );
  }

  /**
   * Get a specific chat session by ID for a guest user
   */
  getGuestChatById(
    hubName: string,
    ipAddress: string,
    chatId: string
  ): Observable<TravelBuddyChatSession> {
    return this.apiService
      .get(`visitors/${hubName}/guests/${encodeURIComponent(ipAddress)}/chats/${chatId}`)
      .pipe(
        tap((chat) => console.log('Guest chat loaded:', chat)),
        catchError((error) => {
          console.error('Error loading guest chat:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Update an existing chat session for a guest user
   */
  updateGuestChat(
    hubName: string,
    ipAddress: string,
    updateData: UpdateChatRequest
  ): Observable<TravelBuddyChatSession> {
    return this.apiService
      .put(`visitors/${hubName}/guests/${encodeURIComponent(ipAddress)}/chats/`, updateData)
      .pipe(
        tap((chat) => console.log('Guest chat updated:', chat)),
        catchError((error) => {
          console.error('Error updating guest chat:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Delete a chat session for a guest user
   */
  deleteGuestChat(hubName: string, ipAddress: string, chatId: string): Observable<void> {
    return this.apiService
      .delete(`visitors/${hubName}/guests/${encodeURIComponent(ipAddress)}/chats/${chatId}`)
      .pipe(
        tap(() => console.log('Guest chat deleted:', chatId)),
        catchError((error) => {
          console.error('Error deleting guest chat:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get recent chat sessions for a guest user (last N chats)
   */
  getGuestRecentChats(
    hubName: string,
    ipAddress: string,
    limit: number = 10
  ): Observable<TravelBuddyChatSession[]> {
    return this.apiService
      .get(`visitors/${hubName}/guests/${encodeURIComponent(ipAddress)}/chats/recent`, { limit })
      .pipe(
        tap((chats) => console.log('Guest recent chats loaded:', chats.length)),
        catchError((error) => {
          console.error('Error loading guest recent chats:', error);
          return of([]);
        })
      );
  }

  /**
   * Get saved/bookmarked chat sessions for a guest user
   */
  getGuestSavedChats(hubName: string, ipAddress: string): Observable<TravelBuddyChatSession[]> {
    return this.getGuestChats(hubName, ipAddress, { isSaved: true }).pipe(
      tap((chats) => console.log('Guest saved chats loaded:', chats.length))
    );
  }

  /**
   * Toggle the saved status of a chat for a guest user
   */
  toggleGuestChatSaved(
    hubName: string,
    ipAddress: string,
    chatId: string,
    isSaved: boolean
  ): Observable<TravelBuddyChatSession> {
    return this.updateGuestChat(hubName, ipAddress, { chatId, isSaved }).pipe(
      tap((chat) => console.log('Guest chat saved status toggled:', chat))
    );
  }

  // ==================== UNIFIED OPERATIONS ====================

  /**
   * Save a chat session - automatically determines if user is logged in or guest
   */
  saveChat(
    hubName: string,
    chatData: SaveChatRequest,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<TravelBuddyChatSession> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.saveUserChat(hubName, userContext.userId, chatData);
    } else if (userContext.ipAddress) {
      return this.saveGuestChat(hubName, userContext.ipAddress, chatData);
    } else {
      return throwError(() => new Error('Either userId or ipAddress must be provided'));
    }
  }

  /**
   * Get all chats - automatically determines if user is logged in or guest
   */
  getChats(
    hubName: string,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean },
    options?: { limit?: number; offset?: number; isSaved?: boolean }
  ): Observable<TravelBuddyChatSession[]> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.getUserChats(hubName, userContext.userId, options);
    } else if (userContext.ipAddress) {
      return this.getGuestChats(hubName, userContext.ipAddress, options);
    } else {
      return of([]);
    }
  }

  /**
   * Get a specific chat - automatically determines if user is logged in or guest
   */
  getChatById(
    hubName: string,
    chatId: string,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<TravelBuddyChatSession> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.getUserChatById(hubName, userContext.userId, chatId);
    } else if (userContext.ipAddress) {
      return this.getGuestChatById(hubName, userContext.ipAddress, chatId);
    } else {
      return throwError(() => new Error('Either userId or ipAddress must be provided'));
    }
  }

  /**
   * Update a chat - automatically determines if user is logged in or guest
   */
  updateChat(
    hubName: string,
    updateData: UpdateChatRequest,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<TravelBuddyChatSession> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.updateUserChat(hubName, userContext.userId, updateData);
    } else if (userContext.ipAddress) {
      return this.updateGuestChat(hubName, userContext.ipAddress, updateData);
    } else {
      return throwError(() => new Error('Either userId or ipAddress must be provided'));
    }
  }

  updateFolder(
    hubName: string,
    userId: string,
    folderId: string,
    newName: string
  ): Observable<any> {
    return this.apiService.put(`visitors/${hubName}/users/${userId}/chats/folders/${folderId}`, {
      name: newName,
    });
  }

  deleteFolder(hubName: string, userId: string, folderId: string): Observable<any> {
    return this.apiService.delete(`visitors/${hubName}/users/${userId}/chats/folders/${folderId}`);
  }

  /**
   * Delete a chat - automatically determines if user is logged in or guest
   */
  deleteChat(
    hubName: string,
    chatId: string,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<void> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.deleteUserChat(hubName, userContext.userId, chatId);
    } else if (userContext.ipAddress) {
      return this.deleteGuestChat(hubName, userContext.ipAddress, chatId);
    } else {
      return throwError(() => new Error('Either userId or ipAddress must be provided'));
    }
  }

  /**
   * Get recent chats - automatically determines if user is logged in or guest
   */
  getRecentChats(
    hubName: string,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean },
    limit: number = 10
  ): Observable<TravelBuddyChatSession[]> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.getUserRecentChats(hubName, userContext.userId, limit);
    } else if (userContext.ipAddress) {
      return this.getGuestRecentChats(hubName, userContext.ipAddress, limit);
    } else {
      return of([]);
    }
  }

  /**
   * Get saved chats - automatically determines if user is logged in or guest
   */
  getSavedChats(
    hubName: string,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<TravelBuddyChatSession[]> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.getUserSavedChats(hubName, userContext.userId);
    } else if (userContext.ipAddress) {
      return this.getGuestSavedChats(hubName, userContext.ipAddress);
    } else {
      return of([]);
    }
  }

  /**
   * Toggle saved status - automatically determines if user is logged in or guest
   */
  toggleChatSaved(
    hubName: string,
    chatId: string,
    isSaved: boolean,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<TravelBuddyChatSession> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.toggleUserChatSaved(hubName, userContext.userId, chatId, isSaved);
    } else if (userContext.ipAddress) {
      return this.toggleGuestChatSaved(hubName, userContext.ipAddress, chatId, isSaved);
    } else {
      return throwError(() => new Error('Either userId or ipAddress must be provided'));
    }
  }

  /**
   * Delete all recent chats - automatically determines if user is logged in or guest
   */
  deleteRecentChats(
    hubName: string,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<{ success: boolean; message: string; deletedCount: number }> {
    if (!userContext.isAnonymous && userContext.userId) {
      return this.deleteUserRecentChats(hubName, userContext.userId);
    } else if (userContext.ipAddress) {
      return this.deleteGuestRecentChats(hubName, userContext.ipAddress);
    } else {
      return throwError(() => new Error('Either userId or ipAddress must be provided'));
    }
  }

  /**
   * Toggle favorite status for an item
   * @param hubName The hub name
   * @param itemObjectId The ID of the item to favorite/unfavorite
   * @param type The type of the favorite item
   * @param name The display name of the favorite item
   * @param googlePlaceId Optional Google Place ID
   * @param userId The user ID
   * @returns Observable with the favorite status and data
   */
  toggleFavorite(data: {
    hubName: string;
    userId: string;
    itemObjectId: string;
    type: string;
    name: string;
    alias: string;
    snapshot: string;
    googlePlaceId: string;
  }) {
    if (data.userId) {
      return this.apiService.post(
        `visitors/${data.hubName}/users/${data.userId}/chats/favorites`,
        data
      );
    }

    return throwError(() => new Error('User must be logged in to manage favorites'));
  }

  /**
   * Get user's favorites for a specific hub
   * @param hubName The name of the hub
   * @param userId The user ID
   * @returns Observable with the list of favorites
   */
  getUserFavorites(hubName: string, userId: string) {
    if (!userId || !hubName) {
      return throwError(() => new Error('User ID and hubName is required'));
    }
    return this.apiService.get(`visitors/${hubName}/users/${userId}/chats/favorites`);
  }

  // ==================== GUEST-TO-USER TRANSFER ====================

  /**
   * Bulk transfer guest chats to a user account.
   * Sends all guest chats in a single request to be saved under the user's account.
   */
  bulkTransferGuestChats(
    hubName: string,
    userId: string,
    chats: SaveChatRequest[]
  ): Observable<{ success: boolean; transferredCount: number; errors?: string[] }> {
    if (!userId || !hubName || !chats.length) {
      return throwError(() => new Error('hubName, userId, and at least one chat are required'));
    }

    return this.apiService
      .post(`visitors/${hubName}/users/${userId}/chats/bulk-transfer`, { chats })
      .pipe(
        tap((result: any) =>
          console.log('Guest chats bulk transferred:', result.transferredCount)
        ),
        catchError((error) => {
          console.error('Error bulk transferring guest chats:', error);
          return throwError(() => error);
        })
      );
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Convert ITravelAssistantMessage[] to TravelBuddyChatMessage[]
   */
  convertMessages(
    messages: (ITravelAssistantMessage & { userQuery?: string })[]
  ): (TravelBuddyChatMessage & { userQuery?: string })[] {
    return messages.map((msg) => {
      if (msg.type === 'AI') {
        return {
          id: msg.id,
          type: msg.type,
          msg: msg.msg,
          name: msg.name,
          model: msg.model,
          suggestions: msg.suggestions,
          questions: msg.questions,
          // errorMsg: msg.errorMsg,
          timeConsumed: msg.timeConsumed,
          placeSearch: this.sanitizePlaceSearch(msg.placeSearch),
          placesMentioned: this.sanitizeMentionedPlaces(msg.placesMentioned),
          eventsMentioned: this.sanitizeMentionedPlaces(msg.eventsMentioned),
          askForEvents: msg.askForEvents,
          askForItinerary: msg.askForItinerary,
          feedback: msg.feedback,
          timestamp: msg.timestamp,
          userQuery: msg.userQuery,
          // isEditable: msg.isEditable,
        };
      }
      return {
        id: msg.id,
        type: msg.type,
        msg: msg.msg,
        timestamp: msg.timestamp,
        name: msg.name,
      };
    });
  }

  private sanitizePlaceSearch(placeSearch: any[] | undefined): any[] | undefined {
    if (!placeSearch) {
      return placeSearch;
    }
    return placeSearch
      .map((place) => {
        if (typeof place === 'string') {
          return place;
        }
        return place?.name || place?.searchedPlace?.name || place?.displayName || '';
      })
      .filter((name) => !!name);
  }

  private sanitizeMentionedPlaces(
    items: any[] | undefined
  ): { name: string; city: string }[] | undefined {
    if (!items) {
      return items;
    }
    return items.map((item) => {
      if (typeof item === 'string') {
        return { name: item, city: '' };
      }
      return {
        name: item?.name || '',
        city: item?.city || item?.address?.city || '',
      };
    });
  }

  /**
   * Generate a unique chat ID
   */
  generateChatId(): string {
    return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get user IP address (to be used for guest identification)
   */
  getUserIpAddress(): Observable<string> {
    return this.apiService.get('common/ip-address').pipe(
      map((response: any) => response.ip || response.ipAddress),
      catchError(() => of('unknown'))
    );
  }

  createFolder(
    hubName: string,
    folderData: { name: string },
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<any> {
    const { userId, ipAddress, isAnonymous } = userContext;

    if (!isAnonymous && userId) {
      // For logged-in users
      return this.apiService.post(`visitors/${hubName}/users/${userId}/chats/folders`, {
        ...folderData,
        hubName,
        userId,
      });
    }

    return throwError(() => new Error('User context is invalid'));
  }

  getChatFolders(
    hubName: string,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ): Observable<any> {
    const { userId, ipAddress, isAnonymous } = userContext;

    if (!isAnonymous && userId) {
      // For logged-in users
      return this.apiService.get(`visitors/${hubName}/users/${userId}/chats/folders`);
    }

    return throwError(() => new Error('User context is invalid'));
  }

  moveChatToFolder(
    hubName: string,
    chatId: string,
    folderId: string,
    userContext: { userId?: string; ipAddress?: string; isAnonymous: boolean }
  ) {
    const { userId, ipAddress, isAnonymous } = userContext;

    if (!isAnonymous && userId) {
      return this.apiService.put(`visitors/${hubName}/users/${userId}/chats/move-to-folder`, {
        chatId,
        folderId,
      });
    }

    return throwError(() => new Error('User context is invalid'));
  }
}
